import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import HeroSection from '../components/HeroSection';
import HowItWorks from '../components/HowItWorks';
import BubbleComposition from '../components/BubbleComposition';
import SEO, { faqSchema } from '../components/SEO';
import { useEmpezar } from '../components/EmpezarModal';
import { forPhotographers, forEnterprise } from '../mock';
import { getLangFromPath, localizePath } from '../i18n';

export default function Home() {
  const { open: openEmpezar } = useEmpezar();
  const { t } = useTranslation();
  const location = useLocation();
  const lang = getLangFromPath(location.pathname);
  const to = (p) => localizePath(p, lang);

  const stats = t('home.statsSection.items', { returnObjects: true }) || [];
  const highlights = t('home.photographerBlock.highlights', { returnObjects: true }) || [];
  const faqs = t('home.faqs', { returnObjects: true }) || [];

  // For each mock entry we use its slug/image and pull copy from i18n.
  const photographerCard = forPhotographers[0];

  return (
    <>
      <SEO
        title={t('home.seoTitle')}
        description={t('home.seoDescription')}
        path="/"
        schema={faqSchema(faqs)}
      />
      <HeroSection />

      <HowItWorks />

      {/* PERFECT FOR ANY EVENT */}
      <section className="py-24 lg:py-32 bg-[#08080c]">
        <div className="container-mi">
          <div className="text-center max-w-3xl mx-auto">
            <div className="text-xs uppercase tracking-[0.2em] text-violet-300 mb-4">{t('home.perfectFor.eyebrow')}</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight">
              {t('home.perfectFor.title')}
            </h2>
            <p className="mt-5 text-lg text-white/60">{t('home.perfectFor.subtitle')}</p>
          </div>

          {/* FOR PHOTOGRAPHERS */}
          <div className="mt-16">
            <div className="flex items-baseline gap-3 mb-8">
              <div className="text-xs uppercase tracking-[0.2em] text-white/40">{t('home.photographerBlock.sectionLabel')}</div>
              <h3 className="font-display text-2xl md:text-3xl font-bold">{t('home.photographerBlock.sectionTitle')}</h3>
              <div className="hidden md:block flex-1 h-px bg-white/10 ml-4" />
              <div className="hidden md:block text-sm text-white/50">{t('home.photographerBlock.sectionSubtitle')}</div>
            </div>
            <Link
              to={to(photographerCard.to || `/servicios/${photographerCard.slug}`)}
              data-testid="home-photographers-card"
              className="card-lift group relative rounded-3xl overflow-hidden border border-white/8 bg-[#111116] block img-zoom"
            >
              <div className="grid md:grid-cols-[1.1fr_1fr] gap-0 min-h-[280px] md:min-h-[340px]">
                <div className="relative overflow-hidden">
                  <img src={photographerCard.image} alt={t('home.photographerBlock.title')} className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-95 transition-opacity" />
                  <div className="absolute inset-0 bg-gradient-to-r from-[#111116] via-[#111116]/40 to-transparent md:bg-gradient-to-l" />
                  <div className="absolute top-5 left-5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-200 text-[10px] font-semibold uppercase tracking-wider">
                    {t('home.photographerBlock.badge')}
                  </div>
                </div>
                <div className="relative p-7 md:p-10 flex flex-col justify-center">
                  <div className="text-xs uppercase tracking-wider text-emerald-300 mb-2">{t('home.photographerBlock.eyebrow')}</div>
                  <h4 className="font-display text-3xl md:text-4xl font-bold leading-tight">{t('home.photographerBlock.title')}</h4>
                  <p className="text-white/70 mt-3 text-base md:text-lg max-w-md leading-relaxed">{t('home.photographerBlock.subtitle')}</p>
                  <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 max-w-md">
                    {highlights.map((h) => (
                      <li key={h} className="flex items-center gap-2 text-sm text-white/80">
                        <div className="w-4 h-4 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shrink-0">
                          <Check className="w-2.5 h-2.5 text-white" strokeWidth={3.5} />
                        </div>
                        {h}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-white group-hover:text-emerald-300 transition-colors">
                    {t('home.photographerBlock.cta')}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </Link>
          </div>

          {/* FOR ENTERPRISE */}
          <div className="mt-20">
            <div className="flex items-baseline gap-3 mb-8">
              <div className="text-xs uppercase tracking-[0.2em] text-white/40">{t('home.enterpriseBlock.sectionLabel')}</div>
              <h3 className="font-display text-2xl md:text-3xl font-bold">{t('home.enterpriseBlock.sectionTitle')}</h3>
              <div className="hidden md:block flex-1 h-px bg-white/10 ml-4" />
              <div className="hidden md:block text-sm text-white/50">{t('home.enterpriseBlock.sectionSubtitle')}</div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {forEnterprise.map((item) => (
                <Link
                  to={to(`/servicios/${item.slug}`)}
                  key={item.slug}
                  data-testid={`home-enterprise-card-${item.slug}`}
                  className="card-lift group relative rounded-3xl overflow-hidden border border-white/8 bg-[#111116] aspect-[3/4] img-zoom"
                >
                  <img src={item.image} alt={t(`home.enterpriseBlock.cards.${item.slug}.title`)} className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-85 transition-opacity" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                  <div className="absolute inset-0 p-6 flex flex-col justify-end">
                    <h4 className="font-display text-xl md:text-2xl font-bold">{t(`home.enterpriseBlock.cards.${item.slug}.title`)}</h4>
                    <p className="text-white/70 mt-2 text-sm">{t(`home.enterpriseBlock.cards.${item.slug}.subtitle`)}</p>
                    <div className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-violet-300">
                      {t('home.enterpriseBlock.seeMore')} <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-24 lg:py-28 bg-[#08080c] border-y border-white/5">
        <div className="container-mi">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-violet-300 mb-4">{t('home.statsSection.eyebrow')}</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight">
              {t('home.statsSection.titleLine1')}
              <br />
              {t('home.statsSection.titleLine2')}
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-8 max-w-5xl mx-auto">
            {stats.map((s) => (
              <div key={s.label} className="text-center">
                <div className="font-display text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-b from-white to-white/60 bg-clip-text text-transparent">
                  {s.value}
                </div>
                <div className="mt-2 text-xs md:text-sm text-white/50 uppercase tracking-wider">{s.label}</div>
              </div>
            ))}
          </div>
          <div className="mt-14 text-center">
            <Link to={to('/request-demo')}>
              <Button className="bg-white text-[#0a0a0f] hover:bg-white/90 h-12 px-7 rounded-full font-semibold">
                {t('home.statsSection.cta')}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 lg:py-28">
        <div className="container-mi">
          <div className="relative overflow-hidden rounded-3xl border border-violet-400/20 bg-gradient-to-br from-[#141425] via-[#101018] to-[#0e0e14] p-10 md:p-16">
            <div className="absolute -top-32 -right-24 w-[500px] h-[500px] bg-violet-500/15 rounded-full blur-[100px]" />
            <div className="absolute -bottom-32 -left-24 w-[500px] h-[500px] bg-fuchsia-500/10 rounded-full blur-[100px]" />
            <div className="relative grid lg:grid-cols-2 gap-10 lg:gap-8 items-center">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-violet-300 mb-4">{t('home.finalCta.eyebrow')}</div>
                <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
                  {t('home.finalCta.titleLine1')}
                  <br />
                  <span className="text-violet-300">{t('home.finalCta.titleHighlight')}</span>
                </h2>
                <p className="mt-5 text-lg text-white/70 max-w-xl">
                  {t('home.finalCta.subtitle')}
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={openEmpezar}
                    className="bg-white text-[#0a0a0f] hover:bg-white/90 h-12 px-6 rounded-full font-semibold"
                  >
                    {t('home.finalCta.ctaStart')}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                  <Link to={to('/request-demo')}>
                    <Button variant="outline" className="h-12 px-6 rounded-full border-white/15 bg-white/5 hover:bg-white/10 text-white">
                      {t('home.finalCta.ctaDemo')}
                    </Button>
                  </Link>
                </div>
              </div>

              <BubbleComposition />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
